/* Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved. */

import { ApiError } from '@/services/requests/core/ApiError';

/** Why the server refused a plan or seat change, when it said. */
export type PreviewRefusal = 'payment_pending' | 'cycle_unavailable';

/**
 * The reasons that are an answer rather than a failure, by the status that
 * carries them. A 409 is the first payment still settling; a 422 naming a
 * price mismatch is the catalogue refusing a cycle the team's subscription
 * cannot move to (BUG-043). Neither is helped by a retry.
 */
const REASON_CODES: Record<number, Record<string, PreviewRefusal>> = {
  409: { subscription_payment_pending: 'payment_pending' },
  422: { plan_price_mismatch: 'cycle_unavailable' },
};

/**
 * Reads the reason out of a failed preview.
 *
 * Only a 409 or 422 carrying a known `reason` is a refusal: the server
 * answered and a retry cannot change it. Every other failure (a status
 * without a known reason, 5xx, network) stays a plain failure and keeps its
 * retry.
 *
 * @param error - Whatever the preview call threw.
 * @returns The refusal, or null when this is an ordinary failure.
 */
export function refusalOf(error: unknown): PreviewRefusal | null {
  if (!(error instanceof ApiError)) return null;
  return REASON_CODES[error.status]?.[error.body?.reason] ?? null;
}
